import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import {
  OrderedSection,
  renderOrderedItems,
  SummaryField,
  SummaryGroup,
  SummaryGroupFieldSet,
  SummarySection,
} from "@smart/bridge-intake-components-dom";
import {
  extractPrefixedTeamId,
  familyProQuestionnaireFormUri,
  ResponseDetails,
} from "@smart/bridge-types-basic";
import { isNotNullOrUndefined } from "@smart/itops-utils-basic";
import {
  MatterSubmission,
  useLoadSetting,
  useLoadStaffDetails,
} from "@smart/manage-gql-client-dom";

import { SubmissionHistory } from "./submission-history";
import { GqlTracker } from "../../types";

type ViewResponsesProps = {
  trackersToDisplay: (GqlTracker | null)[];
  orderedSections: OrderedSection[];
  responses: Record<string, any>;
  matterSubmission: MatterSubmission | undefined;
  loading: boolean;
  showResponsesDetails?: ResponseDetails[];
};

export const ViewResponses = ({
  trackersToDisplay,
  matterSubmission,
  orderedSections,
  responses,
  loading,
  showResponsesDetails,
}: ViewResponsesProps) => {
  const { "LINK-3736": automaticReminders } = useFeatureFlags();
  const setting = useLoadSetting({});
  const userIdsFromTrackers = trackersToDisplay
    .map((tracker) =>
      tracker?.user?.uri
        ? extractPrefixedTeamId(tracker.user.uri, "sb").id
        : undefined,
    )
    .filter(isNotNullOrUndefined);
  const staffDetails = useLoadStaffDetails({
    staffIds: [],
    userIds: [...new Set(userIdsFromTrackers)],
  });

  return !matterSubmission ||
    matterSubmission.formUri === familyProQuestionnaireFormUri ||
    loading ? null : (
    <div className="flex flex-1 overflow-hidden">
      <div className="relative p-[5.6rem] flex-1 overflow-y-auto bg-neutral-50">
        <div className="max-w-[84.9rem] min-h-full mx-auto my-0">
          <div className="p-[3.2rem] rounded border border-neutral-100 bg-white">
            {orderedSections.map(({ orderedItems, section }) => (
              <SummarySection key={section.uri} section={section}>
                {renderOrderedItems({
                  orderedItems,
                  responses,
                  responsesDetails: showResponsesDetails,
                  renderGroup: (props) => <SummaryGroup {...props} />,
                  renderField: (props) => <SummaryField {...props} />,
                  renderGroupFieldSet: (props) => (
                    <SummaryGroupFieldSet {...props} />
                  ),
                })}
              </SummarySection>
            ))}
          </div>
        </div>
      </div>
      <SubmissionHistory
        trackers={trackersToDisplay}
        staffDetails={staffDetails.result}
        automaticReminders={automaticReminders}
        reminderSchedule={setting.result?.reminder}
        submission={{
          status: matterSubmission.status,
          sharedAt: matterSubmission.rawSubmission?.values.sharedAt,
        }}
      />
    </div>
  );
};
